import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@super-resume-new/ui/components/accordion";
import { Button } from "@super-resume-new/ui/components/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@super-resume-new/ui/components/input-group";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  HelpCircle,
  Layers,
  Search,
  ShieldCheck,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { jsonLdScript, seo } from "@/utils/seo";
import { breadcrumbSchema, faqSchema } from "@/utils/schema";

const FAQ_TITLE = "Resume & ATS FAQ — How JobCues Rewrites Your Resume | JobCues";
const FAQ_DESCRIPTION =
  "Answers to how an applicant tracking system reads a resume, why the JobCues rewrite redistributes bullets under a fixed budget, what a defend card is, and how keyword coverage is scored.";

export const Route = createFileRoute("/faq")({
  head: () => {
    const head = seo({
      title: FAQ_TITLE,
      description: FAQ_DESCRIPTION,
      path: "/faq",
      type: "article",
    });
    return {
      ...head,
      scripts: [
        // FAQPage no longer earns a rich result for a commercial site, but it
        // is still the cleanest question/answer signal an answer engine can read.
        jsonLdScript(faqSchema(FAQ_DATA.flatMap((group) => group.entries))),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
        ),
      ],
    };
  },
  component: FaqPageComponent,
});

export type FaqEntry = { q: string; a: string };
export type FaqGroup = {
  id: string;
  label: string;
  blurb: string;
  icon: typeof HelpCircle;
  entries: FaqEntry[];
};

export const FAQ_DATA: FaqGroup[] = [
  {
    id: "rewrite-engine",
    label: "How the Rewrite Works",
    blurb: "What the engine does to your text, and why it restructures your bullet points.",
    icon: Wand2,
    entries: [
      {
        q: "Who actually reads your resume?",
        a: "Three readers, and only two of them matter at this stage. The ATS and recruiter match a literal word list and cannot infer anything from it; Pydantic does not read as Python to them. The hiring manager funds the role and is usually not technical in your specialty, so every line has to make sense to someone who has never written code. Your peers are the third reader, and they do not see the document yet. Writing for them is the error that sinks most resumes.",
      },
      {
        q: "It rewrote my whole resume. Is that a bug?",
        a: "No. This is a redistribution engine working under a fixed bullet budget, not a text polisher. Your facts survive, your sentences do not. Two bullets may merge into one, a peer-facing clause may go, and technology this job never asked for gets demoted to your Skills list. Nothing technical is dropped silently: if a fact is not in a bullet, it is in Skills, and if it fits neither it is listed in the warnings under your result.",
      },
      {
        q: "Why did it add keywords as basic as HTML or SQL?",
        a: "Because the scanner matches letters and cannot infer anything. PostgreSQL does not register as SQL, and React does not register as JavaScript, so the child term has to be spelled out even though you would never bother writing it. This one change typically triples keyword coverage in a single pass. It looks like stating the obvious to an engineer, and it is the only version the scanner credits.",
      },
      {
        q: "Why did my Skills section stop counting toward match scores?",
        a: "A keyword scores only inside a work or project bullet that carries what you used, how you used it, why it mattered in plain words, and where you did it. A Skills list and a summary carry none of that, so a human reader gives them nothing. We keep the Skills block anyway. Parsers read it, and it is a useful parking lot for technology this particular job does not want. It just never counts toward your coverage score.",
      },
      {
        q: "Why do I have fewer bullets than before?",
        a: "Bullets are budgeted by how long you held the role. An internship or a stint under six months gets 3, a long role gets up to 6, and each project gets 2. Under that ceiling a weak bullet has to win its line against a stronger one, which is why some of them get merged. A dense bullet also outperforms two thin ones with a reader who is giving you twenty seconds.",
      },
      {
        q: "Why does a bullet about teamwork take up a slot?",
        a: "Because it earns it. The highest-rated résumé lines in hiring reviews are often ones that name no technology at all — daily meetings, sprint planning, retrospectives and code reviews in a Scrum team, with the feedback folded back into the work. Plenty of job descriptions name Agile, mentoring, or explaining technical topics to non-technical people as hard requirements. When yours does, one bullet covers it, and it sits second in the role instead of buried at the bottom.",
      },
      {
        q: "Why do the bullets all read the same shape?",
        a: "Verb, then a deliverable a non-technical person can picture, then the keywords, then a plain-language reason it mattered. 25 to 35 words. A concrete outcome in plain English reaches the person who signs off on the hire. Peer-facing jargon reaches nobody else in the room. Connectors and opening verbs rotate within each role so it does not read like a template.",
      },
    ],
  },
  {
    id: "modes-honesty",
    label: "Modes & Career Honesty",
    blurb:
      "How far the rewrite travels from your literal words, and where it strictly draws the line.",
    icon: ShieldCheck,
    entries: [
      {
        q: "What do the three Modes do?",
        a: "Mode sets how far the rewrite may travel from what your resume literally says. Light relocates only what you already wrote and lands around 50% coverage; choose it if your resume is already dense and accurate. Balance is the default, adds terms that are true by construction, and reaches 70–80%. Aggressive goes past 90% by promoting skills you listed into bullets and stating practices your role implies. It stretches, and every stretched claim ships with a defend card.",
      },
      {
        q: "Is Aggressive lying on my resume?",
        a: "Light and Balance are true by construction. There is nothing in either that you did not write yourself. Aggressive promotes what is already on your page and states working practices your role implies, which holds up once you have prepared the answer. Preparing that answer is the entire job of the defend cards. The hard limit never moves: no Mode will name a vendor, product, certification, employer or degree you have had zero contact with. Those go to the gap questionnaire, because that is the one category interview prep cannot rescue.",
      },
      {
        q: "What are defend cards and the gap questionnaire?",
        a: "A defend card names a stretched claim, the fact underneath it, and how to answer when someone asks about it. Read them before a screening call. The gap questionnaire handles keywords that could not be placed from your resume at all. Each asks a question only you can answer, with a suggestion that would make the claim true. Answer any of them and regenerate; your answer is folded into a fresh rewrite.",
      },
      {
        q: "Why does the scorecard still list missing keywords?",
        a: "The scorecard counts the target keywords that landed inside a real bullet, and every miss says why it was left out. Some stay missing on purpose, because no honest placement exists yet: a vendor you have never touched, a practice your team does not run. Inventing those is what ends a screening call. The questionnaire is the route to closing them, and for some gaps a weekend of real work is the fastest fix available.",
      },
    ],
  },
  {
    id: "tools",
    label: "Choosing the Right Tool",
    blurb: "Which tool to reach for based on your current resume and target position.",
    icon: Layers,
    entries: [
      {
        q: "How is the ATS Booster different from the Resume Tailor?",
        a: "The Tailor rewrites the text of your resume to make you the ideal candidate on paper. The Booster leaves your document looking exactly as it does now and embeds the job's keywords invisibly into the PDF, so the text an ATS parser extracts carries them. Reach for the Booster when your resume is already well-written and only needs to clear automated filters. Reach for the Tailor when the content itself needs heavy alignment with the job description.",
      },
      {
        q: "What is the Master Career Profile in my Account?",
        a: "Your Master Career Profile is your single source of truth containing all your projects, roles, stack expertise, and metrics. Instead of re-pasting your resume for every single job application, the AI reads directly from your Master Profile, selects the 3-4 most relevant experiences for that specific job, and outputs a tight, professional resume built around it.",
      },
      {
        q: "How does the Jobs Directory prioritize listings?",
        a: "Our job engine continuously parses and filters tech postings across Greenhouse, Lever, Ashby, and LinkedIn. It prioritizes fresh roles (within 7 days) and scores job accessibility so that entry-level, junior, internship, and high-signal remote opportunities are highlighted first.",
      },
      {
        q: "How does the Application Tracker work?",
        a: "The tracker lets you organize your applications through Bookmarked, Applied, Interviewing, Offered, and Rejected stages. You can attach tailored resume variants to specific jobs, track submission dates, and calculate stage conversion rates.",
      },
    ],
  },
  {
    id: "api-mcp",
    label: "API & MCP Integration",
    blurb: "Programmatic access and AI assistant workflows using Model Context Protocol.",
    icon: Sparkles,
    entries: [
      {
        q: "What is the MCP Server?",
        a: "The Model Context Protocol (MCP) server allows AI assistants like Claude Desktop, Cursor, and Windsurf to connect directly to JobCues. Your assistant can check ATS match scores, run tailored rewrites, and query active jobs directly from your IDE prompt.",
      },
      {
        q: "How do API Keys work?",
        a: "You can generate standard API keys with customizable scopes (read jobs, create rewrites, check matches) in the API Keys settings page. Requests use bearer token authentication and deduct credits based on execution.",
      },
    ],
  },
];

function FaqPageComponent() {
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string | "all">("all");

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return FAQ_DATA.map((group) => {
      if (selectedGroup !== "all" && group.id !== selectedGroup) {
        return null;
      }
      if (!q) return group;
      const matchedEntries = group.entries.filter(
        (e) => e.q.toLowerCase().includes(q) || e.a.toLowerCase().includes(q),
      );
      if (matchedEntries.length === 0) return null;
      return {
        ...group,
        entries: matchedEntries,
      };
    }).filter(Boolean) as FaqGroup[];
  }, [search, selectedGroup]);

  return (
    <PageLayout id="faq-page-content" className="max-w-6xl">
      <PageHero
        eyebrow="Help centre"
        icon={HelpCircle}
        title={<strong>Frequently asked questions</strong>}
        description="How the AI reads a resume, covers ATS keywords, and keeps your history honest."
      >
        {/* Search Input */}
        <div className="mx-auto mt-6 max-w-xl text-left">
          <InputGroup className="bg-card shadow-sm border-border">
            <InputGroupAddon align="inline-start">
              <Search className="size-4 text-muted-foreground" />
            </InputGroupAddon>
            <InputGroupInput
              type="text"
              placeholder="Search questions (e.g. keywords, ATS score, bullet points, honesty)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 text-sm font-normal"
            />
            {search && (
              <InputGroupAddon align="inline-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearch("")}
                  className="size-7 p-0 text-muted-foreground hover:text-foreground cursor-pointer rounded-full"
                >
                  <X className="size-3.5" />
                  <span className="sr-only">Clear search</span>
                </Button>
              </InputGroupAddon>
            )}
          </InputGroup>
        </div>

        {/* Category Pills */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={selectedGroup === "all" ? "default" : "outline"}
            onClick={() => setSelectedGroup("all")}
            className={`text-xs h-8 px-3 cursor-pointer ${
              selectedGroup === "all" ? "" : "text-muted-foreground"
            }`}
          >
            All questions
          </Button>
          {FAQ_DATA.map((g) => {
            const Icon = g.icon;
            const isActive = selectedGroup === g.id;
            return (
              <Button
                key={g.id}
                type="button"
                size="sm"
                variant={isActive ? "default" : "outline"}
                onClick={() => setSelectedGroup(g.id)}
                className={`text-xs h-8 px-3 cursor-pointer flex items-center gap-1.5 ${
                  isActive ? "" : "text-muted-foreground"
                }`}
              >
                <Icon className="size-3.5" />
                {g.label}
              </Button>
            );
          })}
        </div>
      </PageHero>

      {/* Accordion Groups */}
      {filteredGroups.length === 0 ? (
        <div className="flex min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center">
          <HelpCircle className="mb-2 size-5 text-muted-foreground" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-foreground">No questions found</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            No FAQ entries matched your search &quot;{search}&quot;. Try adjusting your keywords.
          </p>
          <Button
            onClick={() => {
              setSearch("");
              setSelectedGroup("all");
            }}
            variant="outline"
            size="sm"
            className="mt-4"
          >
            Clear filter
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1  gap-8">
          {filteredGroups.map((group) => {
            const Icon = group.icon;
            return (
              <div key={group.id} className="flex flex-col gap-3">
                <div className="flex items-center gap-2 border-l-2 border-violet-500 pl-3.5">
                  <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{group.label}</h3>
                    <p className="text-xs text-muted-foreground leading-snug">{group.blurb}</p>
                  </div>
                </div>

                <Accordion className="flex flex-col gap-2.5">
                  {group.entries.map((entry) => (
                    <AccordionItem
                      key={entry.q}
                      value={entry.q}
                      className="rounded-xl border border-border bg-card px-4 shadow-sm last:border-b"
                    >
                      <AccordionTrigger className="py-3.5 text-sm font-medium text-foreground text-left">
                        {entry.q}
                      </AccordionTrigger>
                      <AccordionPanel className="pb-4">
                        <p className="text-sm text-muted-foreground leading-relaxed">{entry.a}</p>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom CTA Card */}
      <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-3.5">
          <div className="flex size-10 items-center justify-center rounded-xl bg-brand/10 text-brand-foreground">
            <Wand2 className="size-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Ready to tailor your resume?</h3>
            <p className="text-xs text-muted-foreground">
              Put these ATS optimization principles into practice and generate your PDF in seconds.
            </p>
          </div>
        </div>
        <Link to="/">
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white font-semibold gap-1.5 shrink-0"
          >
            Start Free Tailoring
            <ArrowRight className="size-3.5" />
          </Button>
        </Link>
      </div>
    </PageLayout>
  );
}
